import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SearchInput from '@/components/base/SearchInput';
import VideoCard from '@/components/base/VideoCard';
import FilterPanel from '@/pages/category/components/FilterPanel';
import {
  searchVideos,
  applyFilters,
  sortVideos,
  getHot,
  type FilterState,
} from '@/lib/videos';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [filters, setFilters] = useState<FilterState>({ type: 'all', year: 'all', duration: 'all' });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const results = useMemo(
    () => (q ? sortVideos(applyFilters(searchVideos(q), filters), 'rating') : []),
    [q, filters]
  );
  const hot = getHot(4);

  usePageMeta({
    title: q ? `搜索“${q}” - 男同视频` : '搜索 - 男同视频',
    description: `在男同视频中搜索“${q}”，快速找到相关的男同主题剧情、短片与专题内容。`,
    keywords: `男同视频搜索,${q},男同视频`,
  });

  const handleSearch = (keyword: string) => {
    if (keyword) {
      setSearchParams({ q: keyword });
    } else {
      setSearchParams({});
    }
    setFilters({ type: 'all', year: 'all', duration: 'all' });
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <header className="mt-4 flex flex-col gap-4">
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
          搜索视频内容
        </h1>
        <div className="max-w-2xl">
          <SearchInput defaultValue={q} onSearch={handleSearch} autoFocus />
        </div>
        {q && (
          <p className="text-sm text-foreground-500">
            关键词：<span className="font-semibold text-primary-600">{q}</span> · 共找到{' '}
            <span className="font-bold text-foreground-950">{results.length}</span> 个结果
          </p>
        )}
      </header>

      {!q ? (
        <div className="mt-8 flex flex-col gap-6">
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h3 className="text-base font-bold text-foreground-950">输入关键词开始搜索</h3>
            <p className="max-w-sm text-sm text-foreground-500">
              可以搜索视频标题或简介，例如“海”“夏天”“专题”。
            </p>
          </div>
          <HotRecommend hot={hot} title="大家都在看" />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-50 p-5">
              <FilterPanel
                filters={filters}
                onChange={setFilters}
                onReset={() => setFilters({ type: 'all', year: 'all', duration: 'all' })}
              />
            </div>
          </aside>

          <section>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                匹配到 <span className="font-bold text-primary-600">{results.length}</span> 部内容
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
            </div>

            {results.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-3xl" />
                </span>
                <h3 className="text-base font-bold text-foreground-950">
                  没有找到与“{q}”相关的内容
                </h3>
                <p className="max-w-md text-sm text-foreground-500">
                  试试更换关键词、减少筛选条件，或从下方热门推荐中挑选感兴趣的内容。
                </p>
                <button
                  type="button"
                  onClick={() => setFilters({ type: 'all', year: 'all', duration: 'all' })}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
                <div className="mt-6 w-full text-left">
                  <HotRecommend hot={hot} title="热门推荐" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/40"
          />
          <div className="absolute inset-y-0 right-0 flex w-[82%] max-w-sm flex-col overflow-y-auto bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground-950">筛选</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-500"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters({ type: 'all', year: 'all', duration: 'all' })}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-semibold text-background-50"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function HotRecommend({ hot, title }: { hot: ReturnType<typeof getHot>; title: string }) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
        <i className="ri-fire-line text-accent-500" />
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {hot.map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
      <Link
        to="/category/drama"
        className="flex w-fit items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
      >
        浏览全部分类
        <i className="ri-arrow-right-line" />
      </Link>
    </div>
  );
}